import React, { useMemo } from "react";

import { Avatar, AvatarGroup, Text } from "@performyard/py-components";

import SaveStatus from "./SaveStatus";

import { toAvatarGroupEmployees } from "../../utils/participants";
import { getParticipantName } from "../utils";

import { config } from "../config";
import { useMeetingContext } from "../context";

type NotesSwitcherProps = {
	selectedParticipantId: string;
	onParticipantSelect: (userId: string) => void;
	showSaved: boolean;
	showSaving: boolean;
	hasError: boolean;
};

/**
 * Notes header with AvatarGroup for switching between participants' shared notes.
 * Current user is always first in the avatar list.
 * Replaces the old NotesDropdownHeader component.
 */
const NotesSwitcher = ({
	selectedParticipantId,
	onParticipantSelect,
	showSaved,
	showSaving,
	hasError,
}: NotesSwitcherProps): React.ReactElement => {
	const { userId, workspace, isAdminView, meeting, participantNames } = useMeetingContext();

	const meetingCreatorId = meeting?.created?.userId || userId;
	// In admin (non-participant) view, treat the meeting creator as the "primary"
	// avatar so the switcher leads with their notes instead of the admin's.
	const primaryUserId = isAdminView ? meetingCreatorId : userId;

	// Source the roster from meeting.participants (retains removed users on past
	// meetings) and resolve names via participantNames so removed /
	// deactivated users still render correctly.
	const participants = useMemo(
		() =>
			toAvatarGroupEmployees(meeting.participants, {
				currentUserId: primaryUserId,
				resolveName: (id) => getParticipantName(workspace, participantNames, id),
			}),
		[meeting.participants, primaryUserId, workspace, participantNames]
	);

	const isOwnNotes = selectedParticipantId === userId && !isAdminView;

	const selectedName = useMemo(() => {
		if (isOwnNotes) return "My Notes";
		const name = getParticipantName(workspace, participantNames, selectedParticipantId);
		return `${name || "Participant"}'s Notes`;
	}, [isOwnNotes, selectedParticipantId, workspace, participantNames]);

	const selectedAvatarName = useMemo(
		() => getParticipantName(workspace, participantNames, selectedParticipantId) || "Participant",
		[selectedParticipantId, workspace, participantNames]
	);

	return (
		<div className="flex h-6 items-center justify-between">
			<div className="flex items-center gap-1">
				<Avatar size={config.layout.smallAvatarSize} id={selectedParticipantId} name={selectedAvatarName} />
				<Text size="paragraph-1" weight="semi-bold">
					{selectedName}
				</Text>
			</div>
			<div className="flex items-center gap-2">
				<div className="flex h-5 min-w-[60px] items-center justify-end overflow-hidden">
					{isOwnNotes && <SaveStatus isSaving={showSaving} isSaved={showSaved} hasError={hasError} />}
				</div>
				<AvatarGroup
					employees={participants}
					maxLength={5}
					size={config.layout.smallAvatarSize}
					employeeNameTooltip={true}
					onAvatarClick={(employee) => onParticipantSelect(employee._id)}
					overflowBehavior="dropdown"
					onOverflowSelect={(employee) => onParticipantSelect(employee._id)}
					selectedEmployeeId={selectedParticipantId}
				/>
			</div>
		</div>
	);
};

export default NotesSwitcher;
